import React, { useState, useRef } from 'react';
import { X, Upload, AlertCircle, Download, FileText, Link2 } from 'lucide-react';

interface ImportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: any[]) => void;
}

const ImportDataModal: React.FC<ImportDataModalProps> = ({ isOpen, onClose, onImport }) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const downloadTemplate = () => {
    // Create CSV content
    const headers = [
      'Client_ID',
      'First_Name',
      'Last_Name',
      'Date_of_Birth',
      'Email',
      'Phone',
      'Street_Address',
      'City',
      'State',
      'ZIP',
      'Account_Type',
      'Investment_Goals',
      'Risk_Tolerance',
      'Annual_Income_Range',
      'Net_Worth',
      'Portfolio_Value',
      'Tax_Filing_Status',
      'Financial_Advisor_Name',
      'Last_Contact_Date',
      'Next_Follow_Up',
      'Client_Status',
      'Beneficiaries',
      'Account_Roles',
      'Tags',
      'Client_Notes'
    ].join(',');

    // Sample data row
    const sampleData = [
      'FA12345',
      'John',
      'Smith',
      '01/15/1975',
      'john.smith@example.com',
      '555-123-4567',
      '123 Main Street',
      'Anytown',
      'CA',
      '90210',
      'Individual',
      'Retirement,Education',
      'Moderate',
      '$100,000-$150,000',
      '$750,000',
      '$350,000',
      'Married_Joint',
      'Jane Advisor',
      '05/15/2023',
      '08/15/2023',
      'Active',
      'Sarah Smith, Michael Smith',
      'Primary',
      'VIP,Referral,HighNetWorth',
      'Client prefers morning meetings. Annual review scheduled for October.'
    ].join(',');

    const blob = new Blob([headers + '\n' + sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'financial_advisor_crm_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const validateCSV = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const rows = text.split('\n');
          const headers = rows[0].split(',').map(h => h.trim());
          
          // Validate required headers
          const requiredHeaders = ['First_Name', 'Last_Name', 'Email', 'Client_Status'];
          const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
          
          if (missingHeaders.length > 0) {
            reject(`Missing required columns: ${missingHeaders.join(', ')}`);
            return;
          }

          // Parse and validate data rows
          const data = rows.slice(1)
            .filter(row => row.trim())
            .map((row, index) => {
              const values = row.split(',').map(v => v.trim());
              const record: any = {};
              
              headers.forEach((header, i) => {
                record[header] = values[i] || '';
              });

              // Validate required fields
              if (!record['First_Name']) throw new Error(`Missing First_Name in row ${index + 2}`);
              if (!record['Last_Name']) throw new Error(`Missing Last_Name in row ${index + 2}`);
              if (!record['Email']) throw new Error(`Missing Email in row ${index + 2}`);
              
              // Validate email format
              const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
              if (record['Email'] && !emailRegex.test(record['Email'])) {
                throw new Error(`Invalid email format in row ${index + 2}`);
              }

              // Validate date format if present
              const dateFields = ['Date_of_Birth', 'Last_Contact_Date', 'Next_Follow_Up'];
              dateFields.forEach(field => {
                if (record[field]) {
                  const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
                  if (!dateRegex.test(record[field])) {
                    throw new Error(`Invalid date format for ${field} in row ${index + 2}. Use MM/DD/YYYY format.`);
                  }
                }
              });

              return record;
            });

          resolve(data);
        } catch (error) {
          reject(error.message);
        }
      };

      reader.onerror = () => reject('Error reading file');
      reader.readAsText(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }

    setFile(file);
    setError(null);
    setIsValidating(true);

    try {
      const data = await validateCSV(file);
      setIsValidating(false);
      onImport(data);
      onClose();
    } catch (err) {
      setError(err.toString());
      setIsValidating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-semibold text-gray-900">Import Client Data</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6">
            {/* Template Download */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Step 1: Download Template</h3>
              <div className="flex space-x-3">
                <button
                  onClick={downloadTemplate}
                  className="btn btn-outline flex items-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV Template
                </button>
                <a 
                  href="/dashboard/crm/templates" 
                  className="btn btn-outline flex items-center"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Link2 className="h-4 w-4 mr-2" />
                  View Detailed Template
                </a>
              </div>
            </div>

            {/* Upload Instructions */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Step 2: Prepare Your Data</h3>
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    Use CSV format only
                  </li>
                  <li className="flex items-start">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    Maximum file size: 50MB
                  </li>
                  <li className="flex items-start">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    Required fields: First Name, Last Name, Email, Client Status
                  </li>
                  <li className="flex items-start">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    Dates format: MM/DD/YYYY
                  </li>
                  <li className="flex items-start">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    Currency format: Include $ and commas (e.g., $100,000)
                  </li>
                </ul>
              </div>
            </div>

            {/* File Upload */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Step 3: Upload File</h3>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                    >
                      <span>Upload a file</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        accept=".csv"
                        className="sr-only"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">CSV up to 50MB</p>
                </div>
              </div>

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <div className="mt-2 text-sm text-red-700">{error}</div>
                    </div>
                  </div>
                </div>
              )}

              {isValidating && (
                <div className="mt-4 text-center text-sm text-gray-600">
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-solid border-primary border-r-transparent align-[-0.125em] mr-2"></div>
                  Validating file...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportDataModal;